import { useEffect, useState } from "react";
import {
  Building2, Loader2, Users, UserPlus, Mail, Clock, Ban, Trash2, Eye, RotateCw, Power,
} from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

function Stat({ label, value, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-5">
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-neutral-400">
        <Icon className="h-4 w-4 text-[#C1121F]" /> {label}
      </div>
      <div className="mt-2 text-3xl font-extrabold text-neutral-900">{value}</div>
    </div>
  );
}

export default function Owners() {
  const [data, setData] = useState({ owners: [], invites: [] });
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [detail, setDetail] = useState(null);

  const load = () => {
    api.get("/owners").then(({ data }) => setData(data)).catch(() => {});
    api.get("/platform/stats").then(({ data }) => setStats(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const invite = async () => {
    if (!form.email) { toast.error("Enter an email"); return; }
    setSaving(true);
    try {
      await api.post("/owners/invite", form);
      toast.success(`Invitation sent to ${form.email}`);
      setOpen(false); setForm({ name: "", email: "" }); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const resend = async (email) => {
    try { await api.post("/owners/resend", { email }); toast.success(`Invitation resent to ${email}`); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const toggleSuspend = async (o) => {
    try { await api.patch(`/owners/${o.id}`, { suspended: !o.suspended }); toast.success(o.suspended ? "Owner reactivated" : "Owner suspended"); load(); setDetail(null); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const removeOwner = async (o) => {
    if (!window.confirm(`Delete agency owner "${o.name || o.email}" and their team?`)) return;
    const wipe = window.confirm("Also PERMANENTLY delete all their clients and data?\n\nOK = delete their data too   ·   Cancel = keep their data");
    try { await api.delete(`/owners/${o.id}`, { params: { wipe } }); toast.success("Agency owner removed"); load(); setDetail(null); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/owners/${id}`); setDetail(data); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8" data-testid="owners-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-xl font-bold text-neutral-900">Agencies</h2>
          <p className="text-sm text-neutral-500 mt-1">Create and manage separate agencies. Each owner runs their own private CRM and team.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold" data-testid="invite-owner-button">
              <UserPlus className="h-4 w-4 mr-2" /> Create Owner
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#F6EEEE] border-neutral-200">
            <DialogHeader><DialogTitle className="font-display">Invite an agency owner</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div><Label className="text-neutral-600">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-white border-neutral-200" placeholder="Acme Credit Repair" data-testid="owner-invite-name" /></div>
              <div><Label className="text-neutral-600">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-white border-neutral-200" placeholder="owner@agency.com" data-testid="owner-invite-email" /></div>
              <p className="text-xs text-neutral-500">They'll get an email to set a password and create their own agency workspace with a separate CRM and team.</p>
            </div>
            <DialogFooter>
              <Button onClick={invite} disabled={saving} className="rounded-full bg-[#C1121F] text-white hover:bg-[#9B0E18] font-semibold" data-testid="owner-invite-submit">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Invitation"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="platform-stats">
          <Stat label="Agencies" value={stats.owners} icon={Building2} />
          <Stat label="Total Clients" value={stats.clients} icon={Users} />
          <Stat label="Team Members" value={stats.staff} icon={UserPlus} />
          <Stat label="Disputes" value={stats.disputes} icon={Clock} />
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.owners.map((o) => (
          <div key={o.id} className={`rounded-2xl border bg-white p-5 ${o.suspended ? "border-rose-200 opacity-80" : "border-neutral-200"}`} data-testid="owner-row">
            <div className="flex items-center gap-3">
              <span className="h-11 w-11 rounded-xl bg-[#C1121F]/10 grid place-items-center text-[#C1121F]"><Building2 className="h-5 w-5" /></span>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-neutral-900 truncate">{o.name || o.email}{o.isSelf ? " (you)" : ""}</div>
                <div className="text-xs text-neutral-500 truncate">{o.email}</div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded-md border ${o.suspended ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>{o.status}</span>
            </div>
            <div className="mt-4 flex items-center gap-4 text-sm text-neutral-600">
              <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4 text-neutral-400" /> {o.clients} clients</span>
              <span className="inline-flex items-center gap-1.5"><UserPlus className="h-4 w-4 text-neutral-400" /> {o.staff} staff</span>
            </div>
            <div className="mt-4 flex items-center gap-2">
              <Button onClick={() => openDetail(o.id)} variant="outline" size="sm" className="rounded-full border-neutral-200 bg-neutral-50 hover:bg-neutral-200" data-testid="owner-details-button">
                <Eye className="h-3.5 w-3.5 mr-1.5" /> Details
              </Button>
              {!o.isSelf && (
                <>
                  <Button onClick={() => toggleSuspend(o)} variant="outline" size="sm" className="rounded-full border-neutral-200 bg-neutral-50 hover:bg-neutral-200" data-testid="owner-suspend-button">
                    {o.suspended ? <><Power className="h-3.5 w-3.5 mr-1.5" /> Activate</> : <><Ban className="h-3.5 w-3.5 mr-1.5" /> Suspend</>}
                  </Button>
                  <button onClick={() => removeOwner(o)} className="ml-auto text-neutral-400 hover:text-rose-600" data-testid="owner-delete-button" title="Delete"><Trash2 className="h-4 w-4" /></button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {data.invites.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-3"><Mail className="h-4 w-4 text-[#C1121F]" /><h3 className="font-display font-bold text-neutral-900">Pending owner invitations</h3></div>
          <div className="space-y-3">
            {data.invites.map((iv, i) => (
              <div key={i} className="rounded-xl border border-neutral-200 bg-white p-4 flex items-center justify-between gap-3" data-testid="owner-invite-row">
                <div><div className="font-medium text-neutral-900">{iv.name || iv.email}</div><div className="text-xs text-neutral-500">{iv.email}</div></div>
                <div className="flex items-center gap-3">
                  <span className="text-xs px-2.5 py-1 rounded-md border bg-amber-50 text-amber-700 border-amber-200 inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {iv.status}</span>
                  <Button onClick={() => resend(iv.email)} variant="outline" size="sm" className="rounded-full border-neutral-200 bg-neutral-50 hover:bg-neutral-200" data-testid="owner-resend-button">
                    <RotateCw className="h-3.5 w-3.5 mr-1.5" /> Resend
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Owner detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#F6EEEE] border-neutral-200 max-w-lg">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2">
                  {detail.name || detail.email}
                  <span className={`text-[10px] px-2 py-0.5 rounded-md border ${detail.suspended ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>{detail.status}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="text-sm text-neutral-500">{detail.email}</div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-xl border border-neutral-200 bg-white p-3 text-center"><div className="text-2xl font-bold text-neutral-900">{detail.clients}</div><div className="text-[11px] text-neutral-400">clients</div></div>
                  <div className="rounded-xl border border-neutral-200 bg-white p-3 text-center"><div className="text-2xl font-bold text-neutral-900">{detail.disputes}</div><div className="text-[11px] text-neutral-400">disputes</div></div>
                  <div className="rounded-xl border border-neutral-200 bg-white p-3 text-center"><div className="text-2xl font-bold text-neutral-900">{detail.staff.length}</div><div className="text-[11px] text-neutral-400">staff</div></div>
                </div>
                {detail.staff.length > 0 && (
                  <div>
                    <div className="text-xs font-mono uppercase tracking-wider text-neutral-400 mb-2">Team</div>
                    <div className="space-y-1.5">
                      {detail.staff.map((s) => (
                        <div key={s.id} className="flex items-center justify-between text-sm bg-white border border-neutral-200 rounded-lg px-3 py-2">
                          <span className="text-neutral-800">{s.name || s.email}</span>
                          <span className="text-[11px] text-neutral-500">{s.permission === "view" ? "View only" : "Full access"}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-xs font-mono uppercase tracking-wider text-neutral-400 mb-2">Recent activity</div>
                  {detail.activity.length === 0 ? <div className="text-sm text-neutral-400">No activity yet.</div> : (
                    <div className="max-h-40 overflow-auto rounded-lg border border-neutral-200 bg-white divide-y divide-neutral-100">
                      {detail.activity.map((a, i) => (
                        <div key={i} className="px-3 py-2 text-sm flex justify-between gap-2">
                          <span className="text-neutral-700"><b className="text-neutral-900">{a.actor}</b> {(a.action || "").toLowerCase()}</span>
                          <span className="text-[11px] text-neutral-400 whitespace-nowrap">{a.at ? new Date(a.at).toLocaleDateString() : ""}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {!detail.isSelf && (
                <DialogFooter className="gap-2">
                  <Button onClick={() => toggleSuspend(detail)} variant="outline" className="rounded-full border-neutral-200 bg-neutral-50 hover:bg-neutral-200">
                    {detail.suspended ? "Reactivate" : "Suspend"}
                  </Button>
                  <Button onClick={() => removeOwner(detail)} className="rounded-full bg-rose-600 text-white hover:bg-rose-700 font-semibold">Delete</Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
